import * as z from "zod";

const serviceStatus = z
  .enum([
    "Unsubscribed",
    "Active",
    "PendingActive",
    "Suspended",
    "NoPayment",
    "PendingTermination",
    "PendingCancellation",
    "Disconnected",
  ])
  .optional();

const deliveryType = z
  .enum([
    "COURIER",
    "ESIM",
    "BLISTER",
    "NONE",
    "TECHNICAL_PARTNER_DELIVERY",
    "PREOWNED",
  ])
  .optional();

const modem = z.object({
  specificationId: z.literal("MODEM").optional(),
  offeringName: z.string().optional(),
  characteristic: z.object({
    serialNumber: z.string().optional(),
  }),
  vlocityOfferAlias: z.string().optional(),
  recurringPrice: z.number().optional(),
});

const access = z.object({
  activationDate: z.string().optional(),
  anniversaryDate: z.string().optional(),
  specificationId: z.literal("ACCESS").optional(),
  status: serviceStatus,
  statusChangeDate: z.string().optional(),
  terminationDate: z.string().optional(),
  id: z.string(),
  vlocityOfferAlias: z.string().optional(),
  vlocityOfferId: z.string().optional(),
  recurringPrice: z.number().optional(),
});

const internet = z.object({
  specificationId: z.literal("INTERNET").optional(),
  anniversaryDate: z.string().optional(),
  recurringPrice: z.number().optional(),
  id: z.string(),
  characteristic: z.object({
    login: z.string().optional(),
    extBusinessId: z.string().optional(),
    serviceOption: z.string().optional(),
  }),
  parentId: z.string().optional(),
  vlocityOfferAlias: z.string().optional(),
  balances: z
    .array(
      z.object({
        balanceId: z.string(),
        currentValue: z.string().optional(),
        startingValue: z.string().optional(),
      }),
    )
    .optional(),
});

const additionalTask = z.object({
  specificationId: z.literal("ADDITIONALTASK").optional(),
  vlocityOfferAlias: z.string().optional(),
  recurringPrice: z.number().optional(),
});

const commitment = z.object({
  specificationId: z.literal("COMMITMENT").optional(),
  characteristic: z.object({
    paymentsAmount: z.string().optional(),
  }),
  balances: z.array(
    z.object({
      balanceId: z.string(),
      currentValue: z.string().optional(),
      startingValue: z.string().optional(),
    }),
  ),
  offeringId: z.string().optional(),
  vlocityOfferAlias: z.string().optional(),
  recurringPrice: z.number().optional(),
});

const mobileAccess = z.object({
  specificationId: z.literal("MOBILE_ACCESS").optional(),
  specificationType: z.string().optional(),
  anniversaryDate: z.string().optional(),
  recurringPrice: z.number().optional(),
  statusChangeDate: z.string().optional(),
  terminationDate: z.string().optional(),
  status: serviceStatus,
  recurringPriceDiscount: z.number().optional(),
  resignation14DaysAllowed: z.boolean().optional(),
  isUnsubscribeAllowed: z.boolean().optional(),
  id: z.string(),
  vlocityOfferAlias: z.string().optional(),
  characteristic: z
    .object({
      barringAdmFull: z.string().optional(),
      voiceMailLostCalls: z.string().optional(),
      acr: z.string().optional(),
      roaming: z.string().optional(),
      fraudRoamingBarring: z.string().optional(),
      fraudOutgoingBarring: z.string().optional(),
      msisdn: z.string().optional(),
    })
    .optional(),
});

const simCard = z.object({
  id: z.string().optional(),
  specificationId: z.literal("SIM_CARD").optional(),
  specificationType: z.string().optional(),
  characteristic: z
    .object({
      sim: z.string().optional(),
      simType: z.enum(["PHYSICAL", "ESIM"]).optional(),
      deliveryType,
      login: z.string().optional(),
      extBusinessId: z.string().optional(),
      serviceOption: z.string().optional(),
    })
    .optional(),
  vlocityOfferAlias: z.string().optional(),
  recurringPrice: z.number().optional(),
});

const television = z.object({
  specificationId: z.literal("TELEVISION").optional(),
  specificationType: z.string().optional(),
  recurringPrice: z.number().optional(),
  recurringPriceDiscount: z.number().optional(),
  id: z.string().optional(),
  characteristic: z.object({ login: z.string().optional() }).optional(),
  vlocityOfferAlias: z.string().optional(),
});

const voicePackage = z.object({
  specificationId: z.literal("VOICE_PACKAGE").optional(),
  specificationType: z.string().optional(),
  offeringCategory: z.string().optional(),
  zone: z.string().optional(),
  expiryDate: z.string().optional(),
  balances: z
    .array(
      z.object({
        balanceId: z.string().optional(),
        currentValue: z.string().optional(),
        startingValue: z.string().optional(),
      }),
    )
    .optional(),
  vlocityOfferAlias: z.string().optional(),
  recurringPrice: z.number().optional(),
});

const _5g = z.object({
  specificationId: z.literal("5G").optional(),
  specificationType: z.string().optional(),
  vlocityOfferAlias: z.string().optional(),
  recurringPrice: z.number().optional(),
});

const dataPackage = z.object({
  anniversaryDate: z.string().optional(),
  specificationId: z.literal("DATA_PACKAGE").optional(),
  specificationType: z.string().optional(),
  offeringCategory: z.string().optional(),
  offeringId: z.string().optional(),
  expiryDate: z.string().optional(),
  zone: z.string().optional(),
  balances: z.array(
    z.object({
      balanceId: z.string().optional(),
      currentValue: z.string().optional(),
      startingValue: z.string().optional(),
    }),
  ),
  vlocityOfferAlias: z.string().optional(),
  recurringPrice: z.number().optional(),
});

const sfh = z.object({
  name: z.string().optional(),
  icon: z.string().optional(),
  visibility: z.boolean().optional(),
  paymentAtPurchase: z.boolean().optional(),
  specificationId: z.literal("SFH").optional(),
  specificationType: z.string().optional(),
  recurringPrice: z.number().optional(),
  productOfferingPrice: z
    .object({
      priceType: z.literal("RECURRING").optional(),
      price: z.object({
        amount: z.number().optional(),
      }),
    })
    .optional(),
  vlocityOfferAlias: z.string().optional(),
});
const fSecure = z.object({
  id: z.string(),
  visibility: z.boolean().optional(),
  specificationId: z.literal("FSECURE").optional(),
  specificationType: z.string().optional(),
  recurringPrice: z.number().optional(),
  activationDate: z.string().optional(),
  lastAnniversaryDate: z.string().optional(),
  anniversaryDate: z.string().optional(),
  status: serviceStatus,
  statusChangeDate: z.string().optional(),
  terminationDate: z.string().optional(),
  characteristic: z.object({
    productVariant: z.string().optional(),
  }),
});

const additionalPackage = z.object({
  id: z.string(),
  parentId: z.string(),
  status: serviceStatus,
  visibility: z.boolean().optional(),
  purchaseDate: z.string().optional(),
  activationDate: z.string().optional(),
  lastAnniversaryDate: z.string().optional(),
  anniversaryDate: z.string().optional(),
  offeringId: z.string().optional(),
  offeringName: z.string().optional(),
  offeringCategory: z.string().optional(),
  refillPeriod: z.number().optional(),
  currencyCode: z.string(),
  characteristic: z.object({
    resourceId: z.string(),
  }),
  balances: z.array(
    z.object({
      balanceId: z.string(),
      resourceId: z.string(),
      type: z.enum(["COUNTER", "OTHER"]),
      name: z.string(),
      currentValue: z.string(),
      startingValue: z.string(),
      currentValueWithReservation: z.string(),
    }),
  ),
  source: z.string(),
});

const telcoInflationRate = z.object({
  specificationId: z.literal("TELCO_INFLATION_RATE").optional(),
});

const product = z.union([
  sfh,
  fSecure,
  mobileAccess,
  simCard,
  voicePackage,
  _5g,
  dataPackage,
  modem,
  internet,
  access,
  additionalTask,
  commitment,
  television,
  additionalPackage,
  telcoInflationRate,
]);

export const serverResponse = z.array(product);

// ftth
export type IApiInternet = z.infer<typeof internet>;
export type IAccess = z.infer<typeof access>;
export type IModem = z.infer<typeof modem>;
export type ICommitmentResponse = z.infer<typeof commitment>;
export type IAdditionalTask = z.infer<typeof additionalTask>;
export type ISfh = z.infer<typeof sfh>;

// mobile
export type IMobileAccess = z.infer<typeof mobileAccess>;
export type ISimCard = z.infer<typeof simCard>;
export type IApiVoicePackage = z.infer<typeof voicePackage>;
export type I5G = z.infer<typeof _5g>;
export type IApiDataPackage = z.infer<typeof dataPackage>;
export type ITelevision = z.infer<typeof television>;

// fsecure
export type IFSecure = z.infer<typeof fSecure>;

export type IProduct = z.infer<typeof product>;

// type is extended in /types.ts as a IServiceStatus and should be used as a type in the application.
// IApIServiceStatus is just a type from API
export type IApiServiceStatus = z.infer<typeof serviceStatus>;
